import { useQuery } from "@apollo/client";
import { Refresh } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { ReactElement, useCallback, useState } from "react";

import {
  DeleteInviteTokenMutation,
  DeleteInviteTokenMutationVariables,
  GetInviteTokenQuery,
  GetInviteTokenQueryVariables,
  RefreshInviteTokenMutation,
  RefreshInviteTokenMutationVariables,
} from "~frontend/__generated__/graphql";
import ClickToCopy from "~frontend/components/ClickToCopy";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import {
  DELETE_INVITE_TOKEN,
  GET_INVITE_TOKEN,
  REFRESH_INVITE_TOKEN,
} from "~frontend/graphql/inviteTokens";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

export function InviteLinkDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): ReactElement {
  const { subaccount } = useSubaccount()!;

  const [inviteTokenLoading, setInviteTokenLoading] = useState(false);
  const [inviteTokenError, setInviteTokenError] = useState(false);

  const getInviteTokenQuery = useQuery<
    GetInviteTokenQuery,
    GetInviteTokenQueryVariables
  >(GET_INVITE_TOKEN, {
    variables: { input: { subaccountId: subaccount.id } },
  });

  const [refreshInviteToken] = useMutation<
    RefreshInviteTokenMutation,
    RefreshInviteTokenMutationVariables
  >(REFRESH_INVITE_TOKEN, {
    variables: { input: { subaccountId: subaccount.id } },
    refetchQueries: [GET_INVITE_TOKEN],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Error refreshing invite link",
  });

  const handleRefreshInviteToken = useCallback(async () => {
    setInviteTokenLoading(true);
    const response = await refreshInviteToken();
    setInviteTokenLoading(false);

    if (response.data) {
      setInviteTokenError(false);
    } else {
      setInviteTokenError(true);
    }
  }, [setInviteTokenLoading, refreshInviteToken, setInviteTokenError]);

  const [deleteInviteToken] = useMutation<
    DeleteInviteTokenMutation,
    DeleteInviteTokenMutationVariables
  >(DELETE_INVITE_TOKEN, {
    variables: { input: { subaccountId: subaccount.id } },
    refetchQueries: [GET_INVITE_TOKEN],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: "Error deleting invite link",
  });

  const handleDeleteInviteToken = useCallback(async () => {
    setInviteTokenLoading(true);
    const response = await deleteInviteToken();
    setInviteTokenLoading(false);

    if (response.data?.deleteInviteToken.success) {
      setInviteTokenError(false);
    } else {
      setInviteTokenError(true);
    }
  }, [setInviteTokenLoading, deleteInviteToken, setInviteTokenError]);

  function renderInviteToken() {
    if (inviteTokenLoading || getInviteTokenQuery.loading) {
      return <Loading size={20} />;
    }

    if (inviteTokenError || getInviteTokenQuery.error) {
      return (
        <Alert severity="warning">
          Error fetching invite link.{" "}
          <LinkButton onClick={() => getInviteTokenQuery.refetch()}>
            Retry
          </LinkButton>
        </Alert>
      );
    }

    if (getInviteTokenQuery.data?.getInviteToken) {
      const inviteLink = `${document.location.protocol}//${document.location.host}/join?token=${encodeURIComponent(getInviteTokenQuery.data.getInviteToken.token)}&subaccount=${subaccount.id}`;
      return (
        <Box
          sx={{
            backgroundColor: palette.lightBlue,
            paddingY: "10px",
            paddingX: "16px",
          }}
        >
          <ClickToCopy
            text={inviteLink}
            IconButtonProps={{ sx: { color: palette.blue } }}
            TextProps={{ sx: { color: palette.blue } }}
          >
            {inviteLink!.slice(0, 60)}...
          </ClickToCopy>
        </Box>
      );
    }

    return null;
  }

  function renderActions() {
    if (
      inviteTokenLoading ||
      getInviteTokenQuery.loading ||
      inviteTokenError ||
      getInviteTokenQuery.error
    ) {
      return null;
    }

    if (getInviteTokenQuery.data?.getInviteToken) {
      return (
        <>
          <Button
            variant="contained"
            color="tertiary"
            startIcon={<Refresh />}
            onClick={handleRefreshInviteToken}
          >
            Rotate Invite Link
          </Button>
          <Button
            variant="contained"
            color="tertiary"
            onClick={handleDeleteInviteToken}
          >
            Delete Invite Link
          </Button>
        </>
      );
    }

    return (
      <Button
        variant="contained"
        color="tertiary"
        onClick={handleRefreshInviteToken}
      >
        Create Invite Link
      </Button>
    );
  }

  return (
    <Dialog open={open}>
      <DialogTitle>Get an Invite Link</DialogTitle>
      <DialogContent>
        <Typography marginBottom="8px">
          Use the link below to invite new users to your organization. All users
          who join from this link will be added at the “Texter” level.
        </Typography>
        <Typography marginBottom="16px">
          Rotating the invite link will deactivate the previous link and provide
          a new link to use.
        </Typography>
        {renderInviteToken()}
      </DialogContent>
      <DialogActions sx={{ padding: "0 24px 20px 24px" }}>
        <Button variant="contained" color="tertiary" onClick={onClose}>
          Close
        </Button>
        {renderActions()}
      </DialogActions>
    </Dialog>
  );
}
